import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, mergeMap, retryWhen } from 'rxjs/operators';

/** Returns true when the error is a network failure (status 0) or a server error (5xx). */
export const isNetworkOrServerError = (error: HttpErrorResponse): boolean =>
  error.status === 0 || error.status >= 500;

export interface ExponentialBackoffRetryOptions {
  maxRetries?: number;
  maxDelayMs?: number;
  baseDelayMs?: number;
}

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_MAX_DELAY_MS = 30000;
const DEFAULT_BASE_DELAY_MS = 1000;

export const exponentialBackoffRetry = <E>(
  checkRetry: (error: E) => boolean,
  onRetry: (error: E) => void,
  options?: ExponentialBackoffRetryOptions,
) => {
  const maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES;
  const maxDelayMs = options?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
  const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;

  return <T>(source: Observable<T>): Observable<T> => {
    return source.pipe(
      retryWhen((errors) => {
        return errors.pipe(
          mergeMap((error: E, index) => {
            if (index >= maxRetries) {
              return throwError(error);
            }
            if (checkRetry(error)) {
              onRetry(error);
              const delayMs = Math.min(2 ** index * baseDelayMs, maxDelayMs);
              return of(error).pipe(delay(delayMs));
            }
            return throwError(error);
          }),
        );
      }),
    );
  };
};
